import { useState } from 'react'
import {
    ScoopSpinner,
    ScoopText,
    ScoopButton,
    ScoopDialog,
    ScoopChip,
    ScoopLinearProgress,
    ScoopRow,
    ScoopColumn,
    ScoopAlert,
} from '../../../ui'
import { CheckCircleIcon, CheckIcon, RocketIcon, SparkleIcon } from '@phosphor-icons/react'
import { useBilling } from '../../../../api/hooks/billing/useBilling'
import { useUserInfo } from '../../../../api/hooks/user/useUserInfo'
import { isSubscribed } from '../../../../utils/featureGating'
import { EVENTS, trackEvent } from '../../../../events'
import styles from './BillingSettings.module.scss'

// Tier hierarchy for upgrade logic
const TIER_HIERARCHY = {
    free_trial: 0,
    individual: 1,
    team: 2,
    enterprise: 3,
}

// Plan features configuration
const PLAN_FEATURES = {
    free_trial: {
        name: 'Free Trial',
        price: 0,
        description: '30 days to explore Scoop',
        features: [
            'Up to 5 datasets',
            'Basic visualizations',
            '2 workspaces',
            '1 user',
            '1M rows total',
            '25 AI queries/month',
        ],
    },
    individual: {
        name: 'Individual',
        price: 99,
        description: 'For solo professionals',
        features: [
            'Up to 25 datasets',
            'Advanced visualizations',
            '25 workspaces',
            '1 user',
            '10M rows total',
            '500 AI queries/month',
            'Export & Share enabled',
            'Priority support',
        ],
    },
    team: {
        name: 'Team',
        price: 199,
        priceNote: 'per seat',
        description: 'Collaborate with your team',
        popular: true,
        features: [
            'Everything in Individual',
            'Up to 500 datasets',
            'Up to 25 team members',
            '250 shared workspaces',
            '100M rows total',
            '5,000 AI queries/month',
            'Advanced analytics & ML insights',
            'Slack integration',
            'API access',
        ],
    },
    enterprise: {
        name: 'Enterprise',
        price: null,
        description: 'Custom solutions at scale',
        features: [
            'Everything in Team',
            'Up to 1,000 datasets',
            'Up to 10,000 team members',
            '1,000 workspaces',
            '1B rows total',
            '50,000 AI queries/month',
            'Bring Your Own Key (BYOK)',
            'Dedicated support',
            'Custom SLA',
            'Advanced security',
        ],
    },
}

export const BillingSettings = ({ workspaceMetadata }) => {
    const { user } = useUserInfo()
    const userRole = user?.accountRole || 'member'
    const isAdminOrOwner = ['admin', 'owner'].includes(userRole.toLowerCase())

    const accountInfo = workspaceMetadata?.account
    const isAppSumo = accountInfo?.isAppSumo
    const isFreeTrial = accountInfo?.accountType === 'free_trial'
    const hasSubscription = isSubscribed(accountInfo)
    const currentTier = accountInfo?.accountType

    const { createCheckout, isCreatingCheckout, createPortal, isCreatingPortal } = useBilling()
    const [loadingTier, setLoadingTier] = useState(null)
    const [isPlansModalOpen, setIsPlansModalOpen] = useState(false)

    if (!accountInfo) {
        return (
            <div className={styles.loaderContainer}>
                <ScoopSpinner size={40} />
            </div>
        )
    }

    const handleUpgrade = (tier) => {
        trackEvent(EVENTS.ACCOUNT.UPGRADE, { tier })
        setLoadingTier(tier)
        createCheckout(
            {
                tier,
                accountID: accountInfo.accountID,
            },
            {
                onSuccess: (result) => {
                    if (result && result.checkoutUrl) {
                        // Redirect to Chargebee checkout
                        window.location.href = result.checkoutUrl
                    } else {
                        alert('Failed to create checkout session. Please try again.')
                        setLoadingTier(null)
                    }
                },
                onError: (error) => {
                    alert('Failed to create checkout session. Please try again.')
                    setLoadingTier(null)
                },
            }
        )
    }

    const handleManageBilling = () => {
        setLoadingTier('portal')
        createPortal(
            {
                accountID: accountInfo.accountID,
            },
            {
                onSuccess: (result) => {
                    if (result && result.portalUrl) {
                        // Redirect to Chargebee billing portal
                        window.location.href = result.portalUrl
                    } else {
                        alert('Failed to open billing portal. Please try again.')
                        setLoadingTier(null)
                    }
                },
                onError: (error) => {
                    alert('Failed to open billing portal. Please try again.')
                    setLoadingTier(null)
                },
            }
        )
    }

    const handleContactSales = () => {
        trackEvent(EVENTS.ACCOUNT.CONTACT_SALES)
        window.open('https://www.scoopanalytics.com/contact', '_blank')
    }

    const renderPlanCard = (planKey) => {
        const plan = PLAN_FEATURES[planKey]
        if (!plan) return null

        const isCurrent = currentTier?.toLowerCase() === planKey.toLowerCase()
        const isEnterprise = planKey === 'enterprise'
        const isFreeTrial = planKey === 'free_trial'
        const price = plan.price
        const isLoading = loadingTier === planKey

        // Check if this plan is a lower tier than current (downgrade)
        const currentTierLevel = TIER_HIERARCHY[currentTier?.toLowerCase()] ?? 0
        const targetTierLevel = TIER_HIERARCHY[planKey] ?? 0
        const isDowngrade = targetTierLevel < currentTierLevel

        return (
            <div
                key={planKey}
                className={`${styles.planCard} ${plan.popular ? styles.popularPlan : ''} ${
                    isCurrent ? styles.currentPlan : ''
                } ${isDowngrade ? styles.lowerTierPlan : ''}`}
            >
                {isCurrent ? (
                    <div className={styles.currentPlanBadge}>
                        <CheckCircleIcon size={12} weight="fill" />
                        <ScoopText className={styles.currentPlanBadgeText}>Current Plan</ScoopText>
                    </div>
                ) : (
                    plan.popular && (
                        <div className={styles.popularBadge}>
                            <SparkleIcon size={12} weight="fill" />
                            <ScoopText className={styles.popularBadgeText}>Most Popular</ScoopText>
                        </div>
                    )
                )}

                <ScoopColumn className={styles.planCardHeader}>
                    <ScoopText className={styles.planCardTitle}>{plan.name}</ScoopText>
                    <ScoopText className={styles.planCardDescription}>{plan.description}</ScoopText>

                    <ScoopRow className={styles.priceRow}>
                        {isEnterprise ? (
                            <ScoopText className={styles.contactSalesText}>
                                Custom Pricing
                            </ScoopText>
                        ) : price > 0 ? (
                            <>
                                <ScoopText className={styles.planPrice}>${price}</ScoopText>
                                <ScoopText className={styles.pricePeriod}>
                                    /mo{plan.priceNote && ` ${plan.priceNote}`}
                                </ScoopText>
                            </>
                        ) : (
                            <ScoopText className={styles.planPrice}>Free</ScoopText>
                        )}
                    </ScoopRow>
                </ScoopColumn>

                <ScoopColumn className={styles.planFeatures}>
                    {plan.features.map((feature, index) => (
                        <ScoopRow key={index} className={styles.featureRow}>
                            <CheckIcon
                                size={14}
                                weight="bold"
                                className={styles.featureCheckIcon}
                            />
                            <ScoopText className={styles.featureText}>{feature}</ScoopText>
                        </ScoopRow>
                    ))}
                </ScoopColumn>

                {!isFreeTrial && (
                    <div className={styles.planCardActions}>
                        {isCurrent ? (
                            <ScoopButton
                                variant="outlined"
                                className={styles.manageBillingButton}
                                onClick={isEnterprise ? handleContactSales : handleManageBilling}
                                disabled={!isEnterprise && loadingTier === 'portal'}
                            >
                                {!isEnterprise && loadingTier === 'portal'
                                    ? 'Loading...'
                                    : isEnterprise
                                      ? 'Contact Sales'
                                      : 'Manage Billing'}
                            </ScoopButton>
                        ) : isDowngrade ? (
                            <ScoopButton variant="outlined" disabled>
                                Lower Tier
                            </ScoopButton>
                        ) : isEnterprise ? (
                            <ScoopButton variant="contained" onClick={handleContactSales}>
                                Contact Sales
                            </ScoopButton>
                        ) : (
                            <ScoopButton
                                variant="contained"
                                onClick={() => handleUpgrade(planKey)}
                                disabled={isLoading}
                            >
                                {isLoading ? 'Processing...' : 'Upgrade'}
                            </ScoopButton>
                        )}
                    </div>
                )}
            </div>
        )
    }

    return (
        <div className={styles.container}>
            {/* Account Type Section */}
            <div className={styles.planSection}>
                <ScoopRow className={styles.planHeaderRow}>
                    <div className={styles.planHeader}>
                        <ScoopText className={styles.sectionTitle}>Your Plan</ScoopText>
                        <div className={styles.planBadge}>
                            <ScoopChip color={isAppSumo ? 'success' : 'primary'}>
                                <span className={styles.chipContent}>
                                    {isAppSumo && <CheckCircleIcon size={16} weight="fill" />}
                                    {accountInfo.displayName
                                        ? accountInfo.displayName
                                        : accountInfo.accountType}
                                </span>
                            </ScoopChip>
                            {isAppSumo && (
                                <ScoopText className={styles.trialInfo}>Lifetime Deal</ScoopText>
                            )}
                            {isFreeTrial && (
                                <ScoopText
                                    className={
                                        accountInfo.trialDaysRemaining > 7
                                            ? styles.trialInfo
                                            : styles.trialWarning
                                    }
                                >
                                    {accountInfo.trialDaysRemaining} days remaining in trial
                                </ScoopText>
                            )}
                        </div>
                    </div>
                    <ScoopButton
                        variant={hasSubscription ? 'outlined' : 'contained'}
                        onClick={() => setIsPlansModalOpen(true)}
                        disabled={!isAdminOrOwner}
                    >
                        {hasSubscription ? (
                            'Manage Plan'
                        ) : (
                            <ScoopRow className={styles.buttonContent}>
                                <RocketIcon size={18} weight="fill" />
                                <span>Upgrade Account</span>
                            </ScoopRow>
                        )}
                    </ScoopButton>
                </ScoopRow>
            </div>

            {/* Plan Details */}
            {accountInfo.limits && (
                <div>
                    <ScoopText className={styles.subsectionTitle}>Plan Details</ScoopText>
                    <div className={styles.statsGrid}>
                        <div className={styles.statItem}>
                            <CheckIcon size={16} weight="bold" className={styles.statIcon} />
                            <div className={styles.statContent}>
                                <ScoopText className={styles.statValue}>
                                    {accountInfo.limits.maxDatasets}
                                </ScoopText>
                                <ScoopText className={styles.statLabel}>Datasets</ScoopText>
                                <ScoopText className={styles.statSecondary}>
                                    {accountInfo.usage?.datasets || 0} in use
                                </ScoopText>
                            </div>
                        </div>

                        <div className={styles.statItem}>
                            <CheckIcon size={16} weight="bold" className={styles.statIcon} />
                            <div className={styles.statContent}>
                                <ScoopText className={styles.statValue}>
                                    {accountInfo.currentUsers} / {accountInfo.limits.maxUsers}
                                </ScoopText>
                                <ScoopText className={styles.statLabel}>Team Members</ScoopText>
                                <ScoopText className={styles.statSecondary}>Active users</ScoopText>
                            </div>
                        </div>

                        <div className={styles.statItem}>
                            <CheckIcon size={16} weight="bold" className={styles.statIcon} />
                            <div className={styles.statContent}>
                                <ScoopText className={styles.statValue}>
                                    {(accountInfo.limits.maxTotalRows / 1000000).toFixed(0)}M
                                </ScoopText>
                                <ScoopText className={styles.statLabel}>Rows</ScoopText>
                                <ScoopText className={styles.statSecondary}>
                                    {((accountInfo.usage?.totalRowsStored || 0) / 1000000).toFixed(
                                        1
                                    )}
                                    M stored
                                </ScoopText>
                            </div>
                        </div>

                        {accountInfo.limits.maxAIRequestsPerMonth > 0 && (
                            <div className={styles.statItem}>
                                <CheckIcon size={16} weight="bold" className={styles.statIcon} />
                                <div className={styles.statContent}>
                                    <ScoopText className={styles.statValue}>
                                        {accountInfo.limits.maxAIRequestsPerMonth}
                                    </ScoopText>
                                    <ScoopText className={styles.statLabel}>
                                        AI Queries/Month
                                    </ScoopText>
                                    <ScoopText className={styles.statSecondary}>
                                        {accountInfo.usage?.aiRequestsThisMonth || 0} used
                                    </ScoopText>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Usage Progress */}
            {accountInfo.usage && (
                <div>
                    <ScoopText className={styles.subsectionTitle}>Current Usage</ScoopText>
                    <div className={styles.usageSection}>
                        <div className={styles.usageItem}>
                            <div className={styles.usageHeader}>
                                <ScoopText className={styles.usageLabel}>Datasets</ScoopText>
                                <ScoopText className={styles.usageValue}>
                                    {accountInfo.usage.datasets} /{' '}
                                    {accountInfo.limits?.maxDatasets || 0}
                                </ScoopText>
                            </div>
                            <ScoopLinearProgress
                                variant="determinate"
                                value={Math.min(
                                    100,
                                    (accountInfo.usage.datasets /
                                        (accountInfo.limits?.maxDatasets || 1)) *
                                        100
                                )}
                                className={styles.progressBar}
                            />
                        </div>
                        {accountInfo.limits?.maxAIRequestsPerMonth > 0 && (
                            <div className={styles.usageItem}>
                                <div className={styles.usageHeader}>
                                    <ScoopText className={styles.usageLabel}>AI Queries</ScoopText>
                                    <ScoopText className={styles.usageValue}>
                                        {accountInfo.usage.aiRequestsThisMonth} /{' '}
                                        {accountInfo.limits.maxAIRequestsPerMonth}
                                    </ScoopText>
                                </div>
                                <ScoopLinearProgress
                                    variant="determinate"
                                    value={Math.min(
                                        100,
                                        (accountInfo.usage.aiRequestsThisMonth /
                                            accountInfo.limits.maxAIRequestsPerMonth) *
                                            100
                                    )}
                                    className={styles.progressBar}
                                />
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Features Section */}
            {accountInfo.enabledFeatures && accountInfo.enabledFeatures.length > 0 && (
                <div className={styles.featuresSection}>
                    <ScoopText className={styles.featuresTitle}>Included Features</ScoopText>
                    <div className={styles.featuresContainer}>
                        {accountInfo.enabledFeatures.map((feature) => (
                            <ScoopChip
                                key={feature}
                                label={feature
                                    .replace(/_/g, ' ')
                                    .replace(/\b\w/g, (l) => l.toUpperCase())}
                                size="small"
                                className={styles.featureChip}
                            />
                        ))}
                    </div>
                </div>
            )}

            {/* Billing Message */}
            {isAppSumo && (
                <ScoopAlert severity="success" icon={null}>
                    🎉 You have lifetime access to Scoop through AppSumo. No recurring charges will
                    apply.
                </ScoopAlert>
            )}

            {isFreeTrial && accountInfo.trialDaysRemaining <= 7 && (
                <ScoopAlert severity="warning" icon={null}>
                    ⏰ Your free trial ends in {accountInfo.trialDaysRemaining} days. Upgrade to
                    continue using Scoop.
                </ScoopAlert>
            )}

            {/* Plans Modal */}
            <ScoopDialog
                open={isPlansModalOpen}
                onClose={() => setIsPlansModalOpen(false)}
                maxWidth="xl"
                fullWidth
            >
                <div className={styles.modalContent}>
                    <ScoopColumn className={styles.modalHeader}>
                        <ScoopRow className={styles.plansTitleRow}>
                            {!hasSubscription && (
                                <RocketIcon
                                    size={24}
                                    weight="duotone"
                                    className={styles.rocketIcon}
                                />
                            )}
                            <ScoopText className={styles.modalTitle}>
                                {hasSubscription ? 'Manage your plan' : 'Upgrade your account'}
                            </ScoopText>
                        </ScoopRow>
                        <ScoopText className={styles.plansSubtitle}>
                            {isAppSumo
                                ? 'Upgrade to a direct plan for more features, priority support, and regular updates.'
                                : 'Choose a plan that fits your needs. All plans include a 30-day money-back guarantee.'}
                        </ScoopText>
                    </ScoopColumn>

                    <div className={styles.plansGrid}>
                        {renderPlanCard('free_trial')}
                        {renderPlanCard('individual')}
                        {renderPlanCard('team')}
                        {renderPlanCard('enterprise')}
                    </div>

                    <ScoopText className={styles.plansFooter}>
                        All plans include automatic updates and new features as they're released.
                    </ScoopText>
                </div>
            </ScoopDialog>
        </div>
    )
}
